import { useEffect, useState } from "react";

export type AssetStatus = "loading" | "ready" | "error";

// useAuthedAssetURL fetches a binary asset with the session's bearer token
// and exposes it as a short-lived blob: URL for a plain <img>/<embed> tag.
// A bare <img src="/api/bits/{id}/thumbnail"> can't carry an Authorization
// header, and this app deliberately never puts the access token in a URL
// (it would leak via browser history, server logs, and any Referer header —
// the opposite of the "no long-lived token in localStorage" discipline
// M2's auth design already commits to), so every B&IT thumbnail/original
// goes through an authenticated fetch first.
export function useAuthedAssetURL(path: string, token: string): { url: string | null; status: AssetStatus } {
  const [url, setUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<AssetStatus>("loading");

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setStatus("loading");
    setUrl(null);
    fetch(path, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, token]);

  return { url, status };
}
